import { logError } from '@ardoq/logging';
import { ViewIds } from '@ardoq/api-types';
import {
  CircularLayout,
  CircularLayoutConfig,
  CompactDiskLayout,
  ILayoutAlgorithm,
  LayoutDescriptor,
  OrganicLayout,
  OrthogonalLayout,
  RadialLayout,
  RadialLayoutConfig,
  RadialGroupLayout,
  TreeLayout,
  TreeMapLayout,
  TreeReductionStage,
  HierarchicalLayout,
  HierarchicalLayoutLayeringStrategy,
  SymmetryOptimizationStrategy,
} from '@ardoq/yfiles';
import { ApplyGroupInsetsLayoutStage } from './layoutStage/ApplyGroupInsetsLayoutStage';
import { SplitCombinedEdgesAtTargetGroupStage } from './layoutStage/SplitCombinedEdgesAtTargetGroupStage';

const MAXIMUM_PORT_DEVIATION = 100;

const getLayoutAlgorithm = (
  viewId: ViewIds,
  layoutDescriptor: LayoutDescriptor
) => {
  let layout: ILayoutAlgorithm;
  const { name } = layoutDescriptor;
  switch (name) {
    case 'HierarchicalLayout':
      {
        switch (viewId) {
          case ViewIds.MODERNIZED_BLOCK_DIAGRAM: {
            const hierarchicLayout = new HierarchicalLayout(
              layoutDescriptor.properties
            );
            const { coordinateAssigner } = hierarchicLayout;
            coordinateAssigner.symmetryOptimizationStrategy =
              SymmetryOptimizationStrategy.WEAK;
            coordinateAssigner.maximumPortDeviation = MAXIMUM_PORT_DEVIATION;
            layout = new SplitCombinedEdgesAtTargetGroupStage(
              new ApplyGroupInsetsLayoutStage(hierarchicLayout),
              hierarchicLayout.defaultEdgeDescriptor
            );
            break;
          }
          case ViewIds.BLOCK_DIAGRAM:
          case ViewIds.METAMODEL:
          case ViewIds.ISOMETRIC_BLOCK_DIAGRAM: {
            const hierarchicLayout = new HierarchicalLayout(
              layoutDescriptor.properties
            );
            layout = hierarchicLayout;
            const { coordinateAssigner } = hierarchicLayout;
            coordinateAssigner.symmetryOptimizationStrategy =
              SymmetryOptimizationStrategy.WEAK;
            coordinateAssigner.maximumPortDeviation = MAXIMUM_PORT_DEVIATION;
            break;
          }
          case ViewIds.SWIMLANE: {
            const hierarchicLayout = new HierarchicalLayout(
              layoutDescriptor.properties
            );
            layout = hierarchicLayout;
            hierarchicLayout.fromScratchLayeringStrategy =
              HierarchicalLayoutLayeringStrategy.FROM_SKETCH;
            const { coordinateAssigner } = hierarchicLayout;
            coordinateAssigner.symmetryOptimizationStrategy =
              SymmetryOptimizationStrategy.WEAK;
            hierarchicLayout.fromScratchLayeringStrategy =
              HierarchicalLayoutLayeringStrategy.HIERARCHICAL_OPTIMAL;
            break;
          }
          default:
            layout = new HierarchicalLayout();
            break;
        }
      }
      break;
    case 'CircularLayout': {
      const { edgeBundling = 0, ...circularLayoutConfig } = (
        layoutDescriptor as LayoutDescriptor & {
          properties: CircularLayoutConfig & { edgeBundling?: number };
        }
      ).properties;
      const circularLayout = new CircularLayout(circularLayoutConfig);
      layout = circularLayout;
      circularLayout.edgeBundling.defaultBundleDescriptor.bundled =
        Boolean(edgeBundling);
      circularLayout.edgeBundling.bundlingStrength = edgeBundling;
      break;
    }
    case 'OrganicLayout':
      layout = new OrganicLayout(layoutDescriptor.properties);
      break;
    case 'OrthogonalLayout':
      layout = new OrthogonalLayout(layoutDescriptor.properties);
      break;
    case 'RadialLayout':
      {
        const { edgeBundling = 0, ...circularLayoutConfig } = (
          layoutDescriptor as LayoutDescriptor & {
            properties: RadialLayoutConfig & { edgeBundling?: number };
          }
        ).properties;
        const radialLayout = new RadialLayout(circularLayoutConfig);
        layout = radialLayout;
        radialLayout.edgeBundling.defaultBundleDescriptor.bundled =
          Boolean(edgeBundling);
        radialLayout.edgeBundling.bundlingStrength = edgeBundling;
      }
      break;
    case 'CompactDiskLayout':
      layout = new CompactDiskLayout(layoutDescriptor.properties);
      break;
    case 'RadialGroupLayout':
      layout = new RadialGroupLayout(layoutDescriptor.properties);
      break;
    case 'TreeLayout':
      // the tree reduction stage will reduce the data to a tree structure. without this, it just times out when you have data that doesn't conform to a tree.
      layout = new TreeReductionStage(
        new TreeLayout(layoutDescriptor.properties)
      );
      break;
    case 'TreeMapLayout':
      layout = new TreeMapLayout(layoutDescriptor.properties);
      break;
    default:
      logError(
        Error('Unrecognized layout descriptor in webWorkerLayout.'),
        null,
        { name }
      );
      return;
  }
  return layout;
};
export { getLayoutAlgorithm };
